// Shared market data transform logic used by MarketDataApp and BacktestDashboardApp

export const MARKET_DATA_COLORS = [
  '#072040', '#f0a500', '#599BE3', '#91CF9B', '#ffc107', '#6c757d', '#003366', '#E7E6E6'
];

// freq: 'daily' = daily only, 'monthly' = monthly only, undefined = both
export const TRANSFORMS = [
  { value: 'none',           label: 'None (Raw)' },
  { value: 'normalise',      label: 'Normalise to 100' },
  { value: 'rebase_at',      label: 'Rebase to 100 at Date', needsAnchor: true },
  { value: 'pct_change_1d',  label: '% Change (1D)',   period: 1,   freq: 'daily' },
  { value: 'pct_change_1w',  label: '% Change (1W)',   period: 5,   freq: 'daily' },
  { value: 'pct_change_1m',  label: '% Change (1M)',   period: 21,  freq: 'daily' },
  { value: 'pct_change_3m',  label: '% Change (3M)',   period: 63,  freq: 'daily' },
  { value: 'pct_change_6m',  label: '% Change (6M)',   period: 126, freq: 'daily' },
  { value: 'pct_change_1y',  label: '% Change (1Y)',   period: 252, freq: 'daily' },
  { value: 'chg_1d',         label: 'Change (1D)',     period: 1,   freq: 'daily' },
  { value: 'chg_1w',         label: 'Change (1W)',     period: 5,   freq: 'daily' },
  { value: 'chg_1m',         label: 'Change (1M)',     period: 21,  freq: 'daily' },
  { value: 'chg_3m',         label: 'Change (3M)',     period: 63,  freq: 'daily' },
  { value: 'chg_6m',         label: 'Change (6M)',     period: 126, freq: 'daily' },
  { value: 'chg_1y',         label: 'Change (1Y)',     period: 252, freq: 'daily' },
  { value: 'pct_change_1m_m',label: '% Change (1M)',   period: 1,   freq: 'monthly' },
  { value: 'pct_change_3m_m',label: '% Change (3M)',   period: 3,   freq: 'monthly' },
  { value: 'pct_change_6m_m',label: '% Change (6M)',   period: 6,   freq: 'monthly' },
  { value: 'pct_change_1y_m',label: '% Change (1Y)',   period: 12,  freq: 'monthly' },
  { value: 'chg_1m_m',       label: 'Change (1M)',     period: 1,   freq: 'monthly' },
  { value: 'chg_3m_m',       label: 'Change (3M)',     period: 3,   freq: 'monthly' },
  { value: 'chg_6m_m',       label: 'Change (6M)',     period: 6,   freq: 'monthly' },
  { value: 'chg_1y_m',       label: 'Change (1Y)',     period: 12,  freq: 'monthly' },
  { value: 'yoy',            label: 'Year-over-Year %' },
  { value: 'max_drawdown',   label: 'Maximum Drawdown' },
  { value: 'moving_avg',     label: 'Moving Average',       needsWindow: true, defaultWindow: 20 },
  { value: 'zscore',         label: 'Z-Score (Rolling)',     needsWindow: true, defaultWindow: 60 },
  { value: 'cum_return',     label: 'Cumulative Return' },
  { value: 'rolling_vol',    label: 'Rolling Volatility',   needsWindow: true, defaultWindow: 60, freq: 'daily' },
  { value: 'level_to_return',label: 'Level → Return',  freq: 'daily' },
  { value: 'return_to_level',label: 'Return → Level (100)' },
];

// Get transforms filtered by frequency ('daily' or 'monthly')
export function getTransformsForFreq(tickerFreq) {
  return TRANSFORMS.filter(t => !t.freq || t.freq === tickerFreq);
}

export const PCT_TRANSFORMS = new Set([
  'pct_change_1d', 'pct_change_1w', 'pct_change_1m', 'pct_change_3m',
  'pct_change_6m', 'pct_change_1y',
  'pct_change_1m_m', 'pct_change_3m_m', 'pct_change_6m_m', 'pct_change_1y_m',
  'yoy', 'max_drawdown', 'rolling_vol',
  'level_to_return', 'cum_return',
]);

function transformNormalise(values) {
  const first = values.find(v => v != null);
  if (first == null || first === 0) return values.slice();
  return values.map(v => v != null ? (v / first) * 100 : null);
}

function transformRebaseAt(values, dates, anchor) {
  // Base = first non-null value on or after the anchor date; the WHOLE series
  // (before the anchor too) is rescaled so 100 lands on the anchor.
  if (!anchor || !dates || !dates.length) return values.map(() => null);
  let base = null;
  for (let i = 0; i < dates.length; i++) {
    if (String(dates[i]).slice(0, 10) >= anchor && i < values.length && values[i] != null) {
      base = values[i];
      break;
    }
  }
  if (base == null || base === 0) return values.map(() => null);
  return values.map(v => v != null ? (v / base) * 100 : null);
}

function transformPctChange(values, period) {
  return values.map((v, i) => {
    if (i < period || v == null || values[i - period] == null || values[i - period] === 0) return null;
    return ((v - values[i - period]) / values[i - period]) * 100;
  });
}

// Absolute change over the period, in the series' own units (no % scaling).
function transformAbsChange(values, period) {
  return values.map((v, i) => {
    if (i < period || v == null || values[i - period] == null) return null;
    return v - values[i - period];
  });
}

function transformYoY(values, dates) {
  if (!dates || !dates.length) return values.map(() => null);
  // Pre-compute timestamps once, then binary search for 1-year-ago
  const ts = dates.map(d => new Date(d).getTime());
  const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;
  return values.map((v, i) => {
    if (v == null) return null;
    const targetTime = ts[i] - MS_PER_YEAR;
    // Binary search for closest date <= targetTime
    let lo = 0, hi = i - 1, bestIdx = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (ts[mid] <= targetTime) { bestIdx = mid; lo = mid + 1; }
      else { hi = mid - 1; }
    }
    // Check bestIdx and bestIdx+1 for closest match
    if (bestIdx < 0) return null;
    if (bestIdx + 1 < i && Math.abs(ts[bestIdx + 1] - targetTime) < Math.abs(ts[bestIdx] - targetTime)) {
      bestIdx = bestIdx + 1;
    }
    if (values[bestIdx] == null || values[bestIdx] === 0) return null;
    return ((v - values[bestIdx]) / values[bestIdx]) * 100;
  });
}

function transformMaxDrawdown(values) {
  let peak = null;
  return values.map(v => {
    if (v == null) return null;
    if (peak == null || v > peak) peak = v;
    return ((v - peak) / peak) * 100;
  });
}

function transformMovingAvg(values, window) {
  return values.map((_, i) => {
    if (i < window - 1) return null;
    let sum = 0, count = 0;
    for (let j = i - window + 1; j <= i; j++) {
      if (values[j] != null) { sum += values[j]; count++; }
    }
    return count > 0 ? sum / count : null;
  });
}

function transformZScore(values, window) {
  return values.map((v, i) => {
    if (i < window - 1 || v == null) return null;
    let sum = 0, sumSq = 0, count = 0;
    for (let j = i - window + 1; j <= i; j++) {
      if (values[j] != null) { sum += values[j]; sumSq += values[j] * values[j]; count++; }
    }
    if (count < 2) return null;
    const mean = sum / count;
    const variance = sumSq / count - mean * mean;
    const std = Math.sqrt(Math.max(0, variance));
    return std === 0 ? 0 : (v - mean) / std;
  });
}

function transformCumReturn(values) {
  let result = new Array(values.length).fill(null);
  let prevVal = null;
  let cum = 1;
  for (let i = 0; i < values.length; i++) {
    if (values[i] == null) { result[i] = null; continue; }
    if (prevVal == null) {
      cum = 1;
      result[i] = 100;
    } else {
      const ret = (values[i] - prevVal) / prevVal;
      cum *= (1 + ret);
      result[i] = cum * 100;
    }
    prevVal = values[i];
  }
  return result;
}

function transformRollingVol(values, window) {
  const returns = values.map((v, i) => {
    if (i === 0 || v == null || values[i - 1] == null || values[i - 1] === 0) return null;
    return (v - values[i - 1]) / values[i - 1];
  });
  return returns.map((_, i) => {
    if (i < window) return null;
    let sum = 0, sumSq = 0, count = 0;
    for (let j = i - window + 1; j <= i; j++) {
      if (returns[j] != null) { sum += returns[j]; sumSq += returns[j] * returns[j]; count++; }
    }
    if (count < 2) return null;
    const mean = sum / count;
    const variance = sumSq / count - mean * mean;
    return Math.sqrt(Math.max(0, variance)) * Math.sqrt(252) * 100;
  });
}

function transformLevelToReturn(values) {
  return values.map((v, i) => {
    if (i === 0 || v == null || values[i - 1] == null || values[i - 1] === 0) return null;
    return ((v - values[i - 1]) / values[i - 1]) * 100;
  });
}

function transformReturnToLevel(values) {
  let level = 100;
  return values.map(v => {
    if (v == null) return null;
    level *= (1 + v);
    return level;
  });
}

export function applyTransform(values, type, window, dates, anchor) {
  if (!values || values.length === 0) return values || [];
  const tf = TRANSFORMS.find(t => t.value === type);
  const period = tf?.period;
  switch (type) {
    case 'none': return values.slice();
    case 'normalise': return transformNormalise(values);
    case 'rebase_at': return transformRebaseAt(values, dates, anchor);
    case 'pct_change_1d':
    case 'pct_change_1w':
    case 'pct_change_1m':
    case 'pct_change_3m':
    case 'pct_change_6m':
    case 'pct_change_1y':
    case 'pct_change_1m_m':
    case 'pct_change_3m_m':
    case 'pct_change_6m_m':
    case 'pct_change_1y_m': return transformPctChange(values, period);
    case 'chg_1d':
    case 'chg_1w':
    case 'chg_1m':
    case 'chg_3m':
    case 'chg_6m':
    case 'chg_1y':
    case 'chg_1m_m':
    case 'chg_3m_m':
    case 'chg_6m_m':
    case 'chg_1y_m': return transformAbsChange(values, period);
    case 'yoy': return transformYoY(values, dates);
    case 'max_drawdown': return transformMaxDrawdown(values);
    case 'moving_avg': return transformMovingAvg(values, window || 20);
    case 'zscore': return transformZScore(values, window || 60);
    case 'cum_return': return transformCumReturn(values);
    case 'rolling_vol': return transformRollingVol(values, window || 60);
    case 'level_to_return': return transformLevelToReturn(values);
    case 'return_to_level': return transformReturnToLevel(values);
    default: return values.slice();
  }
}

export function getTransformLabel(type, window, anchor) {
  if (type === 'none') return '';
  const tf = TRANSFORMS.find(t => t.value === type);
  if (!tf) return '';
  if (tf.needsAnchor) return `[Rebased @ ${anchor}]`;
  if (tf.needsWindow) return `[${tf.label} ${window}]`;
  return `[${tf.label}]`;
}

// Explicit tick formatter when any of decimals/pct_suffix/thousands is set on
// the axis config; null otherwise (Chart.js default formatting, as before).
export function makeTickFormatter(axisCfg) {
  const decimals = axisCfg?.decimals;
  const pct = !!axisCfg?.pct_suffix;
  const thousands = !!axisCfg?.thousands;
  if ((decimals === null || decimals === undefined) && !pct && !thousands) return null;
  const dec = (decimals === null || decimals === undefined) ? 0 : decimals;
  return (v) => {
    const num = thousands
      ? v.toLocaleString('en-AU', { minimumFractionDigits: dec, maximumFractionDigits: dec })
      : v.toFixed(dec);
    return pct ? `${num}%` : num;
  };
}

// `extra` (optional, Market Data app only — BacktestDashboardApp passes 3
// args and keeps the legacy look): { gridlines, legend: {show, position},
// stacked }. Axis min/max/decimals/pct_suffix/thousands ride on axisConfig.
export function makeMarketDataChartOptions(axisConfig, hasRightAxis, tickerSettings, extra) {
  const leftType = axisConfig.y.log ? 'logarithmic' : 'linear';
  const rightType = axisConfig.y1?.log ? 'logarithmic' : 'linear';
  const gridlines = extra?.gridlines;   // undefined → legacy grid on both axes
  const legendCfg = extra?.legend;
  const stacked = !!extra?.stacked;
  const yFmt = makeTickFormatter(axisConfig.y);
  const y1Fmt = makeTickFormatter(axisConfig.y1);
  const numOrUndef = (v) => (v === null || v === undefined || v === '' ? undefined : v);

  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      tooltip: {
        backgroundColor: '#072040',
        borderColor: '#f0a500',
        borderWidth: 1,
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        callbacks: {
          title: (items) => {
            if (!items.length) return '';
            return new Date(items[0].label).toLocaleDateString('en-AU');
          },
          label: (ctx) => {
            const val = ctx.parsed.y;
            if (val == null) return '';
            const ticker = ctx.dataset.ticker;
            const s = tickerSettings[ticker];
            const isPct = s && PCT_TRANSFORMS.has(s.transform);
            const formatted = isPct ? val.toFixed(2) + '%' : val.toFixed(4);
            return `${ctx.dataset.label}: ${formatted}`;
          },
        },
      },
      legend: {
        display: legendCfg ? !!legendCfg.show : true,
        position: legendCfg ? (legendCfg.position === 'bottom' ? 'bottom'
          : legendCfg.position === 'right' ? 'right' : 'top') : 'top',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } },
      },
    },
    scales: {
      x: {
        stacked,
        ticks: {
          callback: function (value) {
            const label = this.getLabelForValue(value);
            if (!label) return '';
            const d = new Date(label);
            return d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
          },
          maxTicksLimit: 10,
          font: { size: 10 },
        },
        grid: gridlines === undefined ? { color: '#E7E6E6' } : { display: false },
      },
      y: {
        type: leftType,
        stacked,
        reverse: axisConfig.y.invert,
        min: numOrUndef(axisConfig.y.min),
        max: numOrUndef(axisConfig.y.max),
        position: 'left',
        ticks: { font: { size: 10 }, ...(yFmt ? { callback: (v) => yFmt(v) } : {}) },
        grid: gridlines === undefined ? { color: '#E7E6E6' } : { display: !!gridlines, color: '#E7E6E6' },
      },
      ...(hasRightAxis ? {
        y1: {
          type: rightType,
          stacked,
          reverse: axisConfig.y1?.invert || false,
          min: numOrUndef(axisConfig.y1?.min),
          max: numOrUndef(axisConfig.y1?.max),
          position: 'right',
          ticks: { font: { size: 10 }, ...(y1Fmt ? { callback: (v) => y1Fmt(v) } : {}) },
          grid: { drawOnChartArea: false },
        },
      } : {}),
    },
    elements: { point: { radius: 0 }, line: { borderWidth: 1.5 } },
  };
}
